/**
 * ChartLeftPanel — the patient summary column on the left of the patient record's Chart tab (Antonin, 19 Oct: "the
 * same white 320 px column the encounter shows on the left"). It mounts the encounter's own `PatientSummaryRail` in its
 * `chart` layout and feeds it what the encounter page feeds it, from the same hooks and adapters: the patient summary
 * (`usePatientSummary`), the patient detail (`usePatientDetail`: occupation, education, contacts, importance, header
 * facts). Real data only: nothing the record does not hold is drawn.
 * READ-ONLY: no `patientId` and no `currentUserId` reach the rail, so its write affordances (flags, allergies,
 * problems, procedures, vaccinations, histories, occupation/education) stay off. Outside an encounter there is no
 * permission check on those writes; main's patient record offered none here. Also no encounter and no appointment
 * (no visit line, no status chip, no medication write, no "Add to note"); the patient's name and photo come from the
 * record already loaded by the page.
 *
 * The fold toggle ‹ lives with the page's chart (chart-tab-body.tsx); the layout and look are in the bridge, "patient chart".
 */
import { useCallback } from 'react';
import type { PatientHeaderData } from '@scripsteam/scrips-signal-ds';
import { PatientSummaryRail } from '@/encounter/patient-summary-rail';
import { usePatientSummary } from '@/encounter/services/use-patient-summary';
import { usePatientDetail } from '@/encounter/services/use-patient-detail';
import { patientSummaryToIpsSections, unavailableSummarySections } from '@/encounter/lib/to-ips-sections';
import { toRailEducation, toRailOccupation } from '@/encounter/lib/to-rail-social';
import { toContacts } from '@/encounter/lib/to-patient-contacts';
import { detailToHeader } from '@/encounter/lib/to-patient-header';
import type { Patient } from '@/types/patient-api';

export interface ChartLeftPanelProps {
  patient: Patient;
}

export function ChartLeftPanel({ patient }: ChartLeftPanelProps) {
  const patientId = patient.patientId;
  const summaryQuery = usePatientSummary(patientId);
  const detailQuery = usePatientDetail(patientId);

  const ipsSections = patientSummaryToIpsSections(summaryQuery.data);
  const settled = summaryQuery.isSuccess || (summaryQuery.isError && summaryQuery.data !== undefined);
  const unavailableSections = settled ? unavailableSummarySections(summaryQuery.data) : [];
  const loadError = summaryQuery.isError && summaryQuery.data === undefined;
  const retry = useCallback(() => {
    void summaryQuery.refetch();
  }, [summaryQuery]);

  // The record's own name first (the page has it already), then the live detail over it, as the encounter does.
  const detailHeader = detailToHeader(detailQuery.data);
  const recordName = [patient.firstName, patient.middleName, patient.lastName].filter(Boolean).join(' ').trim();
  const header: PatientHeaderData = { name: recordName || '—', ...detailHeader.patient };
  const demographics = Object.keys(detailHeader.demographics).length ? detailHeader.demographics : undefined;

  return (
    <PatientSummaryRail
      layout="chart"
      patient={header}
      demographics={demographics}
      ipsSections={ipsSections}
      occupation={toRailOccupation(detailQuery.data)}
      education={toRailEducation(detailQuery.data)}
      importance={detailQuery.data?.importance}
      contacts={toContacts(detailQuery.data?.emergencyContactResponse)}
      loading={summaryQuery.isLoading || detailQuery.isLoading}
      loadError={loadError}
      onRetry={retry}
      unavailableSections={unavailableSections}
    />
  );
}
